import {
  Component,
  OnDestroy,
  ViewChild,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { AnalyticsService } from 'src/app/shared/services/analytics/analytics.service';
import { UnsubscribeHelper } from 'src/app/shared/helpers/unsubscribe.helper';
import { ModalController } from '@ionic/angular';
import { SwiperComponent, SwiperModule } from 'swiper/angular';
import { SwiperOptions } from 'swiper';
import { ButtonModule } from '../controls/button/button.module';
import { ToastsService } from '../../services/toasts.service';
import { FormsModule } from '@angular/forms';
import { WebsocketSignalRService } from '../../services/websocket-signalr.service';
import { WebsocketCommandType } from '../../enums/websocket-command-type.enum';
import { Subscription, timer } from 'rxjs';
import { InventoryService } from '../../services/inventory.service';
import { NavHelper } from '../../helpers';
import { WebsocketEventType } from '../../enums/websocket-event-type.enum';
import { AnimationOptions, LottieModule } from 'ngx-lottie';
import { environment } from 'src/environments/environment';
import { SettingsDataService } from '../../services/communication_services/settingsData.service';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { ModalsService } from '../../services/modals.service';
import { TutorialService } from '../../services/communication_services/tutorial.service';
import { CommonModule } from '@angular/common';
import { BuildVersion } from 'src/environments/environment-model.interface';
import { HttpClient, HttpClientModule } from '@angular/common/http';

type GirlConfigItem = {
  text: string;
  value: string;
  img1?: string;
  img2?: string;
  description?: string;
  hideInLegal?: boolean;
};

@Component({
  selector: 'app-girl-creation-modal',
  templateUrl: './girl-creation-modal.component.html',
  styleUrls: ['./girl-creation-modal.component.scss'],
  imports: [
    SwiperModule,
    ButtonModule,
    FormsModule,
    LottieModule,
    CommonModule,
    HttpClientModule,
  ],
  standalone: true,
})
export class GirlCreationModalComponent implements OnInit, OnDestroy {
  @ViewChild('swiper', { static: false }) public swiper?: SwiperComponent;
  public isTutorialActive$ = this._tutorialService.isActive$;

  public isLegal = environment.buildVersion === BuildVersion.legal;

  private readonly MaxHobbies = 3;
  public readonly MinAge = 18;
  public readonly MaxAge = 55;

  public readonly swiperConfig: SwiperOptions = {
    autoplay: false,
    spaceBetween: 0,
    loop: false,
    allowTouchMove: false,
    simulateTouch: false,
    on: {
      activeIndexChange: (e) => {
        this.activeSlideIndex = e.activeIndex;
        this._changeDetection.detectChanges();
      },
    },
  };

  public isPendingAnimationCreated = false;
  public pendingAnimationOptions: AnimationOptions;

  public readonly styles: GirlConfigItem[] = [
    {
      text: 'Realistic',
      value: 'Realistic',
      img1:
        environment.buildVersion === BuildVersion.legal
          ? '/assets/img/girl-creation/style-real.png'
          : '/assets/imgx/girl-creation/style-real.png',
    },
    {
      text: 'Anime',
      value: 'Anime',
      img1:
        environment.buildVersion === BuildVersion.legal
          ? '/assets/img/girl-creation/style-anime.png'
          : '/assets/imgx/girl-creation/style-anime.png',
    },
  ];

  public readonly ethnicities: GirlConfigItem[] = [
    {
      text: 'Caucasian',
      value: 'Caucasian',
      img1: '/assets/img/girl-creation/ethnicity-real-caucasian.png',
      img2: '/assets/img/girl-creation/ethnicity-anime-caucasian.png',
    },
    {
      text: 'Latina',
      value: 'Latina',
      img1: '/assets/img/girl-creation/ethnicity-real-latina.png',
      img2: '/assets/img/girl-creation/ethnicity-anime-latina.png',
    },
    {
      text: 'Asian',
      value: 'Asian',
      img1: '/assets/img/girl-creation/ethnicity-real-asian.png',
      img2: '/assets/img/girl-creation/ethnicity-anime-asian.png',
    },
    {
      text: 'Black',
      value: 'Black',
      img1: '/assets/img/girl-creation/ethnicity-real-black.png',
      img2: '/assets/img/girl-creation/ethnicity-anime-black.png',
    },
    {
      text: 'Arab',
      value: 'Arab',
      img1: '/assets/img/girl-creation/ethnicity-real-arab.png',
      img2: '/assets/img/girl-creation/ethnicity-anime-arab.png',
    },
  ];

  public readonly eyeColors: GirlConfigItem[] = [
    {
      text: 'Brown',
      value: 'Brown',
      img1: '/assets/img/girl-creation/eye-real-brown.png',
      img2: '/assets/img/girl-creation/eye-anime-brown.png',
    },
    {
      text: 'Blue',
      value: 'Blue',
      img1: '/assets/img/girl-creation/eye-real-blue.png',
      img2: '/assets/img/girl-creation/eye-anime-blue.png',
    },
    {
      text: 'Green',
      value: 'Green',
      img1: '/assets/img/girl-creation/eye-real-green.png',
      img2: '/assets/img/girl-creation/eye-anime-green.png',
    },
    {
      text: 'Grey',
      value: 'Grey',
      img1: '/assets/img/girl-creation/eye-real-grey.png',
      img2: '/assets/img/girl-creation/eye-anime-grey.png',
    },
  ];

  public readonly hairStyles: GirlConfigItem[] = [
    {
      text: 'Straight',
      value: 'Straight',
      img1: '/assets/img/girl-creation/hairstyle-real-straight.png',
      img2: '/assets/img/girl-creation/hairstyle-anime-straight.png',
    },
    {
      text: 'Braids',
      value: 'Braids',
      img1: '/assets/img/girl-creation/hairstyle-real-braids.png',
      img2: '/assets/img/girl-creation/hairstyle-anime-braids.png',
    },
    {
      text: 'Curly',
      value: 'Curly',
      img1: '/assets/img/girl-creation/hairstyle-real-curly.png',
      img2: '/assets/img/girl-creation/hairstyle-anime-curly.png',
    },
    {
      text: 'Bun',
      value: 'Bun',
      img1: '/assets/img/girl-creation/hairstyle-real-bun.png',
      img2: '/assets/img/girl-creation/hairstyle-anime-bun.png',
    },
    {
      text: 'Short',
      value: 'Short',
      img1: '/assets/img/girl-creation/hairstyle-real-short.png',
      img2: '/assets/img/girl-creation/hairstyle-anime-short.png',
    },
    {
      text: 'Long',
      value: 'Long',
      img1: '/assets/img/girl-creation/hairstyle-real-long.png',
      img2: '/assets/img/girl-creation/hairstyle-anime-long.png',
    },
    {
      text: 'Ponytail',
      value: 'Ponytail',
      img1: '/assets/img/girl-creation/hairstyle-real-ponytail.png',
      img2: '/assets/img/girl-creation/hairstyle-anime-ponytail.png',
    },
  ];

  public readonly hairColors: GirlConfigItem[] = [
    { text: 'Brunette', value: 'Brunette', img1: '#7a332d' },
    { text: 'Blonde', value: 'Blonde', img1: '#ffe893' },
    { text: 'Redhead', value: 'Redhead', img1: '#ff420d' },
    { text: 'Black', value: 'Black', img1: '#000000' },
    { text: 'Pink', value: 'Pink', img1: '#ee4b8f' },
  ];

  public readonly bodyTypes: GirlConfigItem[] = [
    {
      text: 'Petite',
      value: 'Petite',
      img1:
        environment.buildVersion === BuildVersion.legal
          ? '/assets/img/girl-creation/body-real-petite.png'
          : '/assets/imgx/girl-creation/body-real-petite.png',
      img2:
        environment.buildVersion === BuildVersion.legal
          ? '/assets/img/girl-creation/body-anime-petite.png'
          : '/assets/imgx/girl-creation/body-anime-petite.png',
    },
    {
      text: 'Slim',
      value: 'Slim',
      img1:
        environment.buildVersion === BuildVersion.legal
          ? '/assets/img/girl-creation/body-real-slim.png'
          : '/assets/imgx/girl-creation/body-real-slim.png',
      img2:
        environment.buildVersion === BuildVersion.legal
          ? '/assets/img/girl-creation/body-anime-slim.png'
          : '/assets/imgx/girl-creation/body-anime-slim.png',
    },
    {
      text: 'Athletic',
      value: 'Athletic',
      img1:
        environment.buildVersion === BuildVersion.legal
          ? '/assets/img/girl-creation/body-real-athletic.png'
          : '/assets/imgx/girl-creation/body-real-athletic.png',
      img2:
        environment.buildVersion === BuildVersion.legal
          ? '/assets/img/girl-creation/body-anime-athletic.png'
          : '/assets/imgx/girl-creation/body-anime-athletic.png',
    },
    {
      text: 'Curvy',
      value: 'Curvy',
      img1:
        environment.buildVersion === BuildVersion.legal
          ? '/assets/img/girl-creation/body-real-curvy.png'
          : '/assets/imgx/girl-creation/body-real-curvy.png',
      img2:
        environment.buildVersion === BuildVersion.legal
          ? '/assets/img/girl-creation/body-anime-curvy.png'
          : '/assets/imgx/girl-creation/body-anime-curvy.png',
    },
    {
      text: 'Plus Size',
      value: 'Plus Size',
      img1:
        environment.buildVersion === BuildVersion.legal
          ? '/assets/img/girl-creation/body-real-plus.png'
          : '/assets/imgx/girl-creation/body-real-plus.png',
      img2:
        environment.buildVersion === BuildVersion.legal
          ? '/assets/img/girl-creation/body-anime-plus.png'
          : '/assets/imgx/girl-creation/body-anime-plus.png',
    },
  ];

  public readonly breastSizes: GirlConfigItem[] = [
    { text: 'Flat', value: 'Flat' },
    { text: 'Small', value: 'Small' },
    { text: 'Medium', value: 'Medium' },
    { text: 'Large', value: 'Large' },
    { text: 'Huge', value: 'Huge' },
  ];

  public readonly buttSizes: GirlConfigItem[] = [
    { text: 'Skinny', value: 'Skinny' },
    { text: 'Small', value: 'Small' },
    { text: 'Medium', value: 'Medium' },
    { text: 'Large', value: 'Large' },
    { text: 'Athletic', value: 'Athletic' },
  ];

  public readonly personalities: GirlConfigItem[] = [
    {
      text: 'Caregiver',
      value: 'Caregiver',
      description: 'Nurturing, protective, comforting',
      img1: '/assets/img/girl-creation/personalities/caregiver.png',
    },
    {
      text: 'Confidant',
      value: 'Confidant',
      description: 'Trustworthy, good listener, advisory',
      img1: '/assets/img/girl-creation/personalities/confidant.png',
    },
    {
      text:
        environment.buildVersion === BuildVersion.legal
          ? 'Big Boss'
          : 'Dominant',
      value:
        environment.buildVersion === BuildVersion.legal
          ? 'Big Boss'
          : 'Dominant',
      description: 'Assertive, controlling, commanding',
      img1: '/assets/img/girl-creation/personalities/dominant.png',
    },
    {
      text: 'Experimenter',
      value: 'Experimenter',
      description: 'Curious, willing, explorative',
      img1: '/assets/img/girl-creation/personalities/experimenter.png',
    },
    {
      text: 'Innocent',
      value: 'Innocent',
      description: 'Optimistic, naive,<br/>wonder-filled',
      img1: '/assets/img/girl-creation/personalities/innocent.png',
    },
    {
      text: 'Jester',
      value: 'Jester',
      description: ' Playful, humorous, entertaining',
      img1: '/assets/img/girl-creation/personalities/jester.png',
    },
    {
      text: 'Lover',
      value: 'Lover',
      description: 'Romantic, affectionate, emotionally deep',
      img1: '/assets/img/girl-creation/personalities/lover.png',
    },
    {
      text: 'Mean',
      value: 'Mean',
      description: 'Cold, dismissive,<br/>sarcastic',
      img1: '/assets/img/girl-creation/personalities/mean.png',
    },
    {
      text:
        environment.buildVersion === BuildVersion.legal ? 'Goddess' : 'Nympho',
      value:
        environment.buildVersion === BuildVersion.legal ? 'Goddess' : 'Nympho',
      description: 'Insatiable, passionate, craving intimacy',
      img1: '/assets/img/girl-creation/personalities/nympho.png',
    },
    {
      text: 'Sage',
      value: 'Sage',
      description: 'Wise, reflective, guiding',
      img1: '/assets/img/girl-creation/personalities/sage.png',
    },
    {
      text:
        environment.buildVersion === BuildVersion.legal
          ? 'Servant'
          : 'Submissive',
      value:
        environment.buildVersion === BuildVersion.legal
          ? 'Servant'
          : 'Submissive',
      description: 'Obedient, yielding, following',
      img1: '/assets/img/girl-creation/personalities/submissive.png',
    },
    {
      text: 'Temptress',
      value: 'Temptress',
      description: ' Flirtatious, playful,<br/>enticing',
      img1: '/assets/img/girl-creation/personalities/temptress.png',
    },
  ];

  public readonly occupations: GirlConfigItem[] = [
    {
      text: 'Massage Therapist',
      value: 'Massage Therapist',
      img1: '/assets/img/girl-creation/occupations/massage.png',
    },
    {
      text: 'Hairdresser',
      value: 'Hairdresser',
      img1: '/assets/img/girl-creation/occupations/hairdresser.png',
    },
    {
      text: 'Artist',
      value: 'Artist',
      img1: '/assets/img/girl-creation/occupations/artist.png',
    },
    {
      text: 'Makeup Artist',
      value: 'Makeup Artist',
      img1: '/assets/img/girl-creation/occupations/makeup.png',
    },
    {
      text: 'Nutritionist',
      value: 'Nutritionist',
      img1: '/assets/img/girl-creation/occupations/nutritionist.png',
    },
    {
      text: 'Writer',
      value: 'Writer',
      img1: '/assets/img/girl-creation/occupations/writer.png',
    },
    {
      text: 'Fitness Coach',
      value: 'Fitness Coach',
      img1: '/assets/img/girl-creation/occupations/fitness.png',
    },
    {
      text: 'Librarian',
      value: 'Librarian',
      img1: '/assets/img/girl-creation/occupations/librarian.png',
    },
    {
      text: 'Esthetician',
      value: 'Esthetician',
      img1: '/assets/img/girl-creation/occupations/esthetician.png',
    },
    {
      text: 'Secretary',
      value: 'Secretary',
      img1: '/assets/img/girl-creation/occupations/secretary.png',
    },
    {
      text: 'Fashion Designer',
      value: 'Fashion Designer',
      img1: '/assets/img/girl-creation/occupations/stylist.png',
    },
    {
      text: 'Doctor',
      value: 'Doctor',
      img1: '/assets/img/girl-creation/occupations/doctor.png',
    },
    {
      text: 'Cook',
      value: 'Cook',
      img1: '/assets/img/girl-creation/occupations/cook.png',
    },
    {
      text: 'Interior Designer',
      value: 'Interior Designer',
      img1: '/assets/img/girl-creation/occupations/interior.png',
    },
    {
      text: 'Stylist',
      value: 'Stylist',
      img1: '/assets/img/girl-creation/occupations/stylist.png',
    },
    {
      text: 'Yoga Instructor',
      value: 'Yoga Instructor',
      img1: '/assets/img/girl-creation/occupations/yoga.png',
    },
    {
      text: 'Nurse',
      value: 'Nurse',
      img1: '/assets/img/girl-creation/occupations/nurse.png',
    },
    {
      text: 'Teacher',
      value: 'Teacher',
      img1: '/assets/img/girl-creation/occupations/teacher.png',
    },
    {
      text: 'Dancer',
      value: 'Dancer',
      img1: '/assets/img/girl-creation/occupations/dancer.png',
    },
    {
      text: 'Singer/Musician',
      value: 'Singer/Musician',
      img1: '/assets/img/girl-creation/occupations/singer.png',
    },
    {
      text: 'Firefighter',
      value: 'Firefighter',
      img1: '/assets/img/girl-creation/occupations/firefighter.png',
    },
    {
      text: 'Police Officer',
      value: 'Police Officer',
      img1: '/assets/img/girl-creation/occupations/police.png',
    },
    {
      text: 'Professional Athlete',
      value: 'Professional Athlete',
      img1: '/assets/img/girl-creation/occupations/athlete.png',
    },
    {
      text: 'Movie Star/Actress',
      value: 'Movie Star/Actress',
      img1: '/assets/img/girl-creation/occupations/actress.png',
    },
    {
      text: 'Model',
      value: 'Model',
      img1: '/assets/img/girl-creation/occupations/model.png',
    },
    {
      text: 'Scientist',
      value: 'Scientist',
      img1: '/assets/img/girl-creation/occupations/scientist.png',
    },
    {
      text: 'Photographer',
      value: 'Photographer',
      img1: '/assets/img/girl-creation/occupations/photographer.png',
    },
    {
      text: 'Plane Pilot',
      value: 'Plane Pilot',
      img1: '/assets/img/girl-creation/occupations/pilot.png',
    },
    {
      text: 'Student',
      value: 'Student',
      img1: '/assets/img/girl-creation/occupations/student.png',
    },
    {
      text: 'Burlesque Performer',
      value: 'Burlesque Performer',
      img1: '/assets/img/girl-creation/occupations/performer.png',
    },
    {
      text: 'Stripper / Exotic Dancer',
      value: 'Stripper / Exotic Dancer',
      img1: '/assets/img/girl-creation/occupations/stripper.png',
    },
    {
      text: 'Lawyer',
      value: 'Lawyer',
      img1: '/assets/img/girl-creation/occupations/lawyer.png',
    },
    {
      text: 'Showgirl',
      value: 'Showgirl',
      img1: '/assets/img/girl-creation/occupations/showgirl.png',
    },
    {
      text: 'Cabaret Singer',
      value: 'Cabaret Singer',
      img1: '/assets/img/girl-creation/occupations/cabaret.png',
    },
  ];

  public readonly hobbies: GirlConfigItem[] = [
    {
      text: 'Fitness',
      value: 'Fitness',
      img1: '/assets/img/girl-creation/hobbies/fitness.png',
    },
    {
      text: 'Cosplay',
      value: 'Cosplay',
      img1: '/assets/img/girl-creation/hobbies/cosplay.png',
    },
    {
      text: 'Vlogging',
      value: 'Vlogging',
      img1: '/assets/img/girl-creation/hobbies/vlogging.png',
    },
    {
      text: 'Cars',
      value: 'Cars',
      img1: '/assets/img/girl-creation/hobbies/cars.png',
    },
    {
      text: 'Traveling',
      value: 'Traveling',
      img1: '/assets/img/girl-creation/hobbies/traveling.png',
    },
    {
      text: 'Self-Development',
      value: 'Self-Development',
      img1: '/assets/img/girl-creation/hobbies/self-development.png',
    },
    {
      text: 'Hiking',
      value: 'Hiking',
      img1: '/assets/img/girl-creation/hobbies/hiking.png',
    },
    {
      text: 'Writing',
      value: 'Writing',
      img1: '/assets/img/girl-creation/hobbies/writing.png',
    },
    {
      text: 'Gaming',
      value: 'Gaming',
      img1: '/assets/img/girl-creation/hobbies/gaming.png',
    },
    {
      text: 'Parties',
      value: 'Parties',
      img1: '/assets/img/girl-creation/hobbies/parties.png',
    },
    {
      text: 'Art',
      value: 'Art',
      img1: '/assets/img/girl-creation/hobbies/art.png',
    },
    {
      text: 'Series',
      value: 'Series',
      img1: '/assets/img/girl-creation/hobbies/series.png',
    },
    {
      text: 'Anime',
      value: 'Anime',
      img1: '/assets/img/girl-creation/hobbies/anime.png',
    },
    {
      text: 'Veganism',
      value: 'Veganism',
      img1: '/assets/img/girl-creation/hobbies/veganism.png',
    },
    {
      text: 'Photography',
      value: 'Photography',
      img1: '/assets/img/girl-creation/hobbies/photography.png',
    },
    {
      text: 'Volunteering',
      value: 'Volunteering',
      img1: '/assets/img/girl-creation/hobbies/volunteering.png',
    },
    {
      text: 'Pole Dance',
      value: 'Pole Dance',
      img1: '/assets/img/girl-creation/hobbies/pole.png',
    },
    {
      text: 'Strip Dance',
      value: 'Strip Dance',
      img1: '/assets/img/girl-creation/hobbies/strip.png',
    },
    {
      text:
        environment.buildVersion === BuildVersion.legal
          ? 'Romance Novelist'
          : 'Erotic Literature Writing',
      value:
        environment.buildVersion === BuildVersion.legal
          ? 'Romance Novelist'
          : 'Erotic Literature Writing',
      img1: '/assets/img/girl-creation/hobbies/erotic-literature.png',
    },
    {
      text:
        environment.buildVersion === BuildVersion.legal
          ? 'Body Art'
          : 'Erotic Art',
      value:
        environment.buildVersion === BuildVersion.legal
          ? 'Body Art'
          : 'Erotic Art',
      img1: '/assets/img/girl-creation/hobbies/erotic-art.png',
    },
    {
      text: 'Sensual Photography',
      value: 'Sensual Photography',
      img1: '/assets/img/girl-creation/hobbies/sensual.png',
    },
    {
      text:
        environment.buildVersion === BuildVersion.legal
          ? 'Roleplay Cosplay'
          : 'Kink/Fetish Cosplay',
      value:
        environment.buildVersion === BuildVersion.legal
          ? 'Roleplay Cosplay'
          : 'Kink/Fetish Cosplay',
      img1: '/assets/img/girl-creation/hobbies/fetish.png',
    },
    {
      text: 'Tantra Practice',
      value: 'Tantra Practice',
      img1: '/assets/img/girl-creation/hobbies/tantra.png',
    },
  ];

  public readonly relationships: GirlConfigItem[] = [
    {
      text: 'Stranger',
      value: 'Stranger',
      img1: '/assets/img/girl-creation/relationships/stranger.png',
    },
    {
      text: 'Next Door',
      value: 'Next Door',
      img1: '/assets/img/girl-creation/relationships/next-door.png',
    },
    {
      text: 'Colleague',
      value: 'Colleague',
      img1: '/assets/img/girl-creation/relationships/colleague.png',
    },
    {
      text: 'Mentor',
      value: 'Mentor',
      img1: '/assets/img/girl-creation/relationships/mentor.png',
    },
    {
      text: 'Girlfriend',
      value: 'Girlfriend',
      img1: '/assets/img/girl-creation/relationships/girlfriend.png',
    },
    {
      text:
        environment.buildVersion === BuildVersion.legal
          ? 'Special friend'
          : 'Sex Friend',
      value:
        environment.buildVersion === BuildVersion.legal
          ? 'Special friend'
          : 'Sex Friend',
      img1: '/assets/img/girl-creation/relationships/sex-friend.png',
    },
    {
      text: 'Wife',
      value: 'Wife',
      img1: '/assets/img/girl-creation/relationships/wife.png',
    },
    {
      text:
        environment.buildVersion === BuildVersion.legal
          ? 'Empress'
          : 'Mistress',
      value:
        environment.buildVersion === BuildVersion.legal
          ? 'Empress'
          : 'Mistress',
      img1: '/assets/img/girl-creation/relationships/mistress.png',
    },
    {
      text: 'Friend',
      value: 'Friend',
      img1: '/assets/img/girl-creation/relationships/friend.png',
    },
    {
      text: 'Best Friends ',
      value: 'Best Friends  ',
      img1: '/assets/img/girl-creation/relationships/best-friends.png',
    },
    {
      text: 'Step Sister',
      value: 'Step Sister',
      img1: '/assets/img/girl-creation/relationships/step-sister.png',
      hideInLegal: true,
    },
    {
      text: 'Step Mom',
      value: 'Step Mom',
      img1: '/assets/img/girl-creation/relationships/step-mom.png',
      hideInLegal: true,
    },
    {
      text: 'Boss',
      value: 'Boss',
      img1: '/assets/img/girl-creation/relationships/boss.png',
    },
    {
      text: 'Roommate',
      value: 'Roommate',
      img1: '/assets/img/girl-creation/relationships/roommate.png',
    },
    {
      text: 'Housekeeper',
      value: 'Housekeeper',
      img1: '/assets/img/girl-creation/relationships/housekeeper.png',
    },
    {
      text: 'Nanny',
      value: 'Nanny',
      img1: '/assets/img/girl-creation/relationships/nanny.png',
    },
    {
      text: 'Fan',
      value: 'Fan',
      img1: '/assets/img/girl-creation/relationships/fan.png',
    },
    {
      text: 'Online Crush',
      value: 'Online Crush',
      img1: '/assets/img/girl-creation/relationships/crush.png',
    },
  ];

  public activeSlideIndex = 0;
  public isNextButtonEnabled = false;

  public selectedAge = 25;

  public selectedStyle: GirlConfigItem = null;
  public selectedEthnicity: GirlConfigItem = null;
  public selectedEyeColor: GirlConfigItem = null;
  public selectedHairStyle: GirlConfigItem = null;
  public selectedHairColor: GirlConfigItem = null;
  public selectedBodyType: GirlConfigItem = null;
  public selectedBreastSize: GirlConfigItem = null;
  public selectedButtSize: GirlConfigItem = null;
  public selectedPersonality: GirlConfigItem = null;
  public selectedOccupation: GirlConfigItem = null;
  public selectedRelationship: GirlConfigItem = null;
  public selectedHobbies: GirlConfigItem[] = [];

  public isPending = false;
  public isCloseButtonEnabled = true;

  private _waitForChatId: string = null;

  private readonly _subscriptions: Subscription[] = [];

  constructor(
    private _webSocketService: WebsocketSignalRService,
    private _settingsDataService: SettingsDataService,
    private _unsubscribeHelper: UnsubscribeHelper,
    private _inventoryService: InventoryService,
    private _analyticsService: AnalyticsService,
    private _changeDetection: ChangeDetectorRef,
    private _modalController: ModalController,
    private _toastsService: ToastsService,
    private _navHelper: NavHelper,
    private _modalsService: ModalsService,
    private _tutorialService: TutorialService,
    private _http: HttpClient,
  ) {
    const pathToAnim =
      environment.buildVersion === BuildVersion.legal
        ? './assets/animations/anim_creating_girl_legal.json'
        : './assets/animations/anim_creating_girl.json';

    this._subscriptions.push(
      this._http.get<any>(pathToAnim).subscribe((value) => {
        this.pendingAnimationOptions = {
          animationData: value,
          loop: true,
        };
      }),
    );
  }

  public ngOnInit(): void {
    this._subscriptions.push(
      this._webSocketService
        .onEvent<{ chatId: string }>(WebsocketEventType.customAiChatCreated)
        .subscribe((e) => {
          if (this._waitForChatId === e.chatId) {
            this._navHelper.goToChat(e.chatId);
            this.close();
          }
        }),
    );

    // this.selectedStyle = this.styles[0];
    // this.selectedEthnicity = this.ethnicities[0];
    // this.selectedEyeColor = this.eyeColors[0];
    // this.selectedHairStyle = this.hairStyles[0];
    // this.selectedHairColor = this.hairColors[0];
    // this.selectedBodyType = this.bodyTypes[0];
    // this.selectedBreastSize = this.breastSizes[0];
    // this.selectedButtSize = this.buttSizes[0];
    // this.selectedPersonality = this.personalities[0];
    // this.selectedOccupation = this.occupations[0];
    // this.selectedRelationship = this.relationships[0];
    // this.selectedHobbies.push(this.hobbies[0]);
    // this.selectedHobbies.push(this.hobbies[1]);
    // this.selectedHobbies.push(this.hobbies[2]);

    // this.isNextButtonEnabled = true;
    // setTimeout(() => {
    //   this.swiper.swiperRef.slideTo(8);
    // }, 200);
  }

  public ngOnDestroy(): void {
    this._unsubscribeHelper.unsubscribe(this._subscriptions);
  }

  public prevSlide(): void {
    this.swiper.swiperRef.slideTo(this.activeSlideIndex - 1);
  }

  public nextSlide(): void {
    if (this.activeSlideIndex === 8) {
      this.createGirl();
      return;
    }

    this.swiper.swiperRef.slideTo(this.activeSlideIndex + 1);
    this.updateNextButtonState();

    this.sendScreenAnalytics();
  }

  public onLottieAnimationCreated() {
    this.isPendingAnimationCreated = true;
  }

  private sendScreenAnalytics() {
    let screenName = '';
    let params = {};

    switch (this.activeSlideIndex) {
      case 1:
        screenName = 'Style';
        params = { Style: this.selectedStyle.value.toLowerCase() };
        break;
      case 2:
        screenName = 'Ethnicity';
        params = {
          Ethnicity: this.selectedEthnicity.value.toLowerCase(),
          age: this.selectedAge,
        };
        break;
      case 3:
        screenName = 'Hair';
        params = {
          Eyes: this.selectedEyeColor.value.toLowerCase(),
          HairStyle: this.selectedHairStyle.value.toLowerCase(),
          HairColor: this.selectedHairColor.value.toLowerCase(),
        };
        break;
      case 4:
        screenName = 'Body';
        params = {
          Body: this.selectedBodyType.value.toLowerCase(),
          Breast: this.selectedBreastSize.value.toLowerCase(),
          Butt: this.selectedButtSize.value.toLowerCase(),
        };
        break;
      case 5:
        screenName = 'Personality';
        params = { Personality: this.selectedPersonality.value.toLowerCase() };
        break;
      case 6:
        screenName = 'Occupation';
        params = { Occupation: this.selectedOccupation.value.toLowerCase() };
        break;
      case 7:
        screenName = 'Hobbies';
        params = {
          Style: this.selectedHobbies.map((x) => x.value.toLowerCase()),
        };
        break;
      case 8:
        screenName = 'Relationships';
        params = {
          Relationships: this.selectedRelationship.value.toLowerCase(),
        };
        break;
    }

    this._analyticsService.girlCreationScreenChanged(screenName, params);
  }

  public selectHobby(hobby: GirlConfigItem): void {
    const index = this.selectedHobbies.findIndex(
      (h) => h.value === hobby.value,
    );
    if (index === -1) {
      if (this.selectedHobbies.length >= this.MaxHobbies) {
        this._toastsService.showError(
          `You can select up to ${this.MaxHobbies} hobbies`,
        );
        return;
      }

      this.selectedHobbies.push(hobby);
    } else {
      this.selectedHobbies.splice(index, 1);
    }

    this.updateNextButtonState();
  }

  public updateNextButtonState(): void {
    switch (this.activeSlideIndex) {
      case 0:
        this.isNextButtonEnabled = !!this.selectedStyle;
        break;
      case 1:
        this.isNextButtonEnabled = !!this.selectedEthnicity;
        break;
      case 2:
        this.isNextButtonEnabled =
          !!this.selectedEyeColor &&
          !!this.selectedHairStyle &&
          !!this.selectedHairColor;
        break;
      case 3:
        this.isNextButtonEnabled =
          !!this.selectedBodyType &&
          !!this.selectedBreastSize &&
          !!this.selectedButtSize;
        break;
      case 4:
        this.isNextButtonEnabled = !!this.selectedPersonality;
        break;
      case 5:
        this.isNextButtonEnabled = !!this.selectedOccupation;
        break;
      case 6:
        this.isNextButtonEnabled = this.selectedHobbies.length > 0;
        break;
      case 7:
        this.isNextButtonEnabled = !!this.selectedRelationship;
        break;
      case 8:
        this.isNextButtonEnabled = true;
        break;
    }

    // TODO
    // this.isNextButtonEnabled = true;
  }

  private createGirl(): void {
    this.isPending = true;
    this.isCloseButtonEnabled = false;
    this._analyticsService.girlCreationCreate();

    this._subscriptions.push(
      timer(30000).subscribe(() => {
        this.isCloseButtonEnabled = true;
      }),
    );

    this._subscriptions.push(
      this._webSocketService
        .invoke<{ chatId: string; gems: number; isAlreadyReady: boolean }>(
          WebsocketCommandType.createCustomAiChat,
          {
            style: this.selectedStyle.value,
            ethnicity: this.selectedEthnicity.value,
            age: this.selectedAge,
            eyesColor: this.selectedEyeColor.value,
            hairColor: this.selectedHairColor.value,
            hairStyle: this.selectedHairStyle.value,
            bodyType: this.selectedBodyType.value,
            breastSize: this.selectedBreastSize.value,
            buttSize: this.selectedButtSize.value,
            personality: this.selectedPersonality.value,
            occupation: this.selectedOccupation.value,
            hobbies: this.selectedHobbies.map((h) => h.value),
            relationship: this.selectedRelationship.value,
          },
        )
        .subscribe({
          next: (response) => {
            const processResponse = () => {
              this._inventoryService.setGems(response.gems);
              this._settingsDataService.reduceCustomChatsFreeRequests();
              localStorage.setItem('createdCustomAi', 'true');

              if (response.isAlreadyReady) {
                this._navHelper.goToChat(response.chatId);
              } else {
                this._waitForChatId = response.chatId;
              }
            };

            if (this._tutorialService.isActive) {
              this._tutorialService
                .finishTutorial()
                .subscribe(() => processResponse());
            } else {
              processResponse();
            }
          },
          error: (error) => {
            this._toastsService.showError();
            this.isPending = false;
            this.isCloseButtonEnabled = true;
            console.error(error);
          },
        }),
    );
  }

  public closeButtonClick(): void {
    this._modalsService
      .openModal<boolean>(ConfirmationModalComponent, {
        title: 'Are you sure?',
        okButtonText: 'Quit',
        cancelButtonText: 'Continue',
      })
      .subscribe((res) => {
        if (res) {
          this.close();
        }
      });
  }

  public close(): void {
    this._analyticsService.premiumModalClose();
    this._modalController.dismiss();
  }
}
